import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  output,
  Output,
  ViewChild,
} from '@angular/core';
import { toCanvas } from 'html-to-image';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import { Comparison } from 'app/providers/bridge-api/dashboard.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { decimalsToPipeDigitInfo } from '../widget-predictive/predictive.utils';
import { WidgetPredictiveOptions } from '../widget-predictive/widget-predictive.component';
import { IExportCanvas } from '../../models/export-canvas';
import { Breakdown } from '../../models/predictive.models';

@EnableClassTelemetry()
@Component({
  selector: 'app-breakdown-table',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './breakdown-table.component.html',
  styleUrls: ['./breakdown-table.component.scss'],
})
export class BreakdownTableComponent implements OnInit, IExportCanvas {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() breakdowns!: Breakdown[];

  @Input() focusedBreakdown!: Breakdown;
  @Input() predictiveOptions!: WidgetPredictiveOptions;
  @Input() isReadOnly!: boolean;
  @Input() isDashboardLocked!: boolean;
  @Input() comparison?: Comparison;

  @Output() select = new EventEmitter<Breakdown>();
  @Output() resetBreakdownSimulation = new EventEmitter<Breakdown>();

  hideBreakdown = output<Breakdown>();

  @ViewChild('table', { read: ElementRef }) table: ElementRef<HTMLTableElement>;

  readonly DISPLAYED_COLUMNS = [
    'percent',
    'breakdown',
    'current',
    'simulated',
    'focused',
    'actions',
  ];
  readonly TOP_COLUMNS = ['percent-top', 'breakdown-top', 'score', 'settings'];
  readonly DETAILS_COLUMN = ['details'];
  readonly allBreakdownName = Constants.TWTRA_ALL_BREAKDOWN;

  private _selectedBreakdown?: Breakdown = null;

  numberPipeDigitInfo: string;
  scoringKey: string;

  ngOnInit(): void {
    this.numberPipeDigitInfo = decimalsToPipeDigitInfo(this.predictiveOptions.decimals);
    this.scoringKey = this.predictiveOptions.mainSettings.scoring.toLowerCase();
  }

  onHideBreakdown(breakdown: Breakdown): void {
    this.hideBreakdown.emit(breakdown);
  }

  onSelectBreakdown(breakdown: Breakdown): void {
    breakdown.isExpanded = !breakdown.isExpanded;
    this._selectedBreakdown = breakdown;
    this.select.emit(breakdown);
  }

  isSelectedBreakdown(breakdown: Breakdown): boolean {
    return this._selectedBreakdown === breakdown;
  }

  shouldShowFocusedIcon(breakdown: Breakdown): boolean {
    if (this._selectedBreakdown) {
      return this.isSelectedBreakdown(breakdown);
    }

    return this.focusedBreakdown.name === breakdown.name;
  }

  onResetBreakdownSimulation(breakdown: Breakdown): void {
    this.resetBreakdownSimulation.emit(breakdown);
  }

  canResetSimulation(breakdown: Breakdown): boolean {
    return breakdown.drivers.some((d) => d.simulated != null);
  }

  exportCanvas(): Promise<HTMLCanvasElement> {
    return toCanvas(this.table.nativeElement);
  }
}
